import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { Table, Segmented, DatePicker } from "antd";
import API from "../../utils/api";
import dayjs from "dayjs";
import Loader from "../../components/Loader";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useSearchParams } from "react-router-dom";

const ScoreboardOrders = () => {
  const { manager } = useParams();
  const managerName = decodeURIComponent(manager || "");
  const [searchParams, setSearchParams] = useSearchParams();

  const [range, setRange] = useState(searchParams.get("range") || "daily");
  const [date, setDate] = useState(
    searchParams.get("date") ? dayjs(searchParams.get("date")) : null,
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.set("range", range);
      if (date) params.set("date", date.format("YYYY-MM-DD"));

      // ✅ IMPORTANT: use the param route (matches backend router)
      const res = await API.get(
        `/api/scoreboard/orders/${encodeURIComponent(managerName)}?${params.toString()}`,
      );

      setRows(res?.data?.data || []);
      setLabel(res?.data?.label || "");
    } catch (e) {
      console.error(e);
      setRows([]);
      setLabel("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sp = {};
    sp.range = range;
    if (date) sp.date = date.format("YYYY-MM-DD");
    setSearchParams(sp);
    fetchOrders();
    // eslint-disable-next-line
  }, [range, date, managerName]);

  const columns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (d) => dayjs(d).format("DD/MM/YYYY"),
      },
      {
        title: "Enrollment",
        dataIndex: "enrollment",
        key: "enrollment",
        width: 140,
      },
      { title: "Order ID", dataIndex: "orderId", key: "orderId", width: 200 },
      {
        title: "Tracking",
        dataIndex: "trackingId",
        key: "trackingId",
        width: 160,
      },
      {
        title: "Partner",
        dataIndex: "deliveryPartner",
        key: "deliveryPartner",
        width: 140,
      },
      {
        title: "Amount",
        dataIndex: "finalAmount",
        key: "finalAmount",
        width: 120,
      },
      { title: "Status", dataIndex: "status", key: "status", width: 110 },
    ],
    [],
  );
  const getRowClassName = (status) => {
    switch (status) {
      case "HMI":
        return "bg-red-50";
      case "In Progress":
        return "bg-yellow-50";
      case "SHIPPED":
        return "bg-green-50";
      case "NEW":
        return "bg-gray-50";
      case "Recieved":
        return "bg-emerald-50";
      case "Schedule":
        return "bg-sky-50";
      case "RTD":
        return "bg-blue-50";
      case "PNA":
        return "bg-orange-50";
      case "Archived":
        return "bg-neutral-200";
      default:
        return "";
    }
  };

  return (
    <AdminLayout>
      <div className="p-4">
        {/* 🔥 Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between 
                 mb-4 w-full py-4 px-4 
                 bg-gradient-to-r from-blue-500 to-red-300 
                 shadow-lg rounded-lg"
        >
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl font-bold text-neutral-50"
            >
              {managerName}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xs text-neutral-200"
            >
              {label ? `Showing: ${label}` : ""}
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-xs text-neutral-200"
              >
                , Total: {rows.length}
              </motion.span>
            </motion.p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* ✅ Date filter */}
            <DatePicker
              value={date}
              onChange={(v) => setDate(v)}
              allowClear
              format="DD MMM YYYY"
              placeholder="Pick date"
              disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            />

            <motion.div
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Segmented
                value={range}
                onChange={setRange}
                options={[
                  { label: "Today", value: "daily" },
                  { label: "This Week", value: "weekly" },
                  { label: "This Month", value: "monthly" },
                ]}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* 🔥 Loader ↔ Table Animation */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Loader />
            </motion.div>
          ) : (
            <motion.div
              key={`table-${range}-${date ? date.format("YYYY-MM-DD") : "no-date"}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="rounded-xl overflow-hidden"
            >
              <Table
                rowKey={(r) => r._id}
                dataSource={rows}
                columns={columns}
                bordered
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 100 }}
                rowClassName={(record) =>
                  `${getRowClassName(record.status)} transition-all hover:shadow-md hover:-translate-y-[1px] duration-200`
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default ScoreboardOrders;
